import { useState } from "react";
import { Badge, Button, Card, Grid, Group, SegmentedControl, Skeleton, Stack, Text, Textarea, Title } from "@mantine/core";
import { IconSparkles } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { flattenMealResult, type IngredientExtractOutput } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FoodDetailDrawer, MealGroupsView } from "../components/food";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { AiContextFields } from "../components/AiContextFields";
import { composeMessage } from "../lib/clientSettings";

type Mode = "extract" | "structured" | "actions";

const EXAMPLES: Record<Mode, string[]> = {
  extract: [
    "For lunch I had a turkey club sandwich with fries and a diet coke",
    "Breakfast: 2 scrambled eggs, a slice of sourdough with butter and a flat white. Dinner: chicken stir fry with rice",
    "a bowl of pho with extra beef and a side of spring rolls",
  ],
  structured: [
    "A cheeseburger with fries and a vanilla milkshake",
    "Grilled salmon with roasted potatoes and a green salad with vinaigrette",
    "Chicken tikka masala with basmati rice, garlic naan and a mango lassi",
  ],
  actions: ["Log two boiled eggs and a banana for breakfast", "Remove the fries from lunch and add a side salad instead", "Yesterday dinner I had salmon with quinoa"],
};

const actionColor = { add: "lime.8", remove: "red", none: "gray" } as const;

export function TextToFoodsPage() {
  const [mode, setMode] = useState<Mode>("extract");
  const [text, setText] = useState("");
  const [selected, setSelected] = useState<IngredientExtractOutput | null>(null);
  const [note, setNote] = useState("");
  const extract = useMutation({ mutationFn: (t: string) => passio.tools.extractIngredients(t, composeMessage(note)) });
  const structured = useMutation({ mutationFn: (t: string) => passio.tools.extractFoodAndIngredients(t, composeMessage(note)) });
  const actions = useMutation({ mutationFn: (t: string) => passio.tools.extractMealLogAction(t, composeMessage(note)) });
  const active = { extract, structured, actions }[mode];
  const run = (t = text) => {
    if (!t.trim()) return;
    setText(t);
    active.mutate(t);
  };

  return (
    <RequireFeature feature="text_ai">
      <PageHeader
        eyebrow="POST /napi/tools/extractIngredients · /extractFoodAndIngredients · /extractMealLogAction"
        title="Text to foods"
        description="Free text (typed or transcribed voice) to structured, database-matched foods. When the text describes meals, items keep their meal and component names so you can rebuild the meal structure."
        actions={<RouteModelPicker routes={["extractIngredients", "extractFoodAndIngredients", "extractMealLogAction"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <SegmentedControl
                fullWidth
                value={mode}
                onChange={(v) => setMode(v as Mode)}
                data={[
                  { value: "extract", label: "Extract foods" },
                  { value: "structured", label: "Structured meal" },
                  { value: "actions", label: "Meal-log actions" },
                ]}
              />
              <Textarea
                autosize
                minRows={4}
                placeholder={mode === "actions" ? "Tell the log what to add or remove…" : "Describe what you ate…"}
                value={text}
                onChange={(e) => setText(e.currentTarget.value)}
              />
              {mode === "structured" && (
                <Text fz="xs" c="dimmed">
                  One meal back, split into components (main, side, drink) with linked ingredients under each.
                </Text>
              )}
              <AiContextFields note={note} onNoteChange={setNote} placeholder="e.g. this was at a restaurant" />
              <Button leftSection={<IconSparkles size={16} />} onClick={() => run()} loading={active.isPending} disabled={!text.trim()}>
                {{ extract: "Extract foods", structured: "Build meal", actions: "Get actions" }[mode]}
              </Button>
              <Stack gap={6}>
                <Text fz="xs" c="dimmed">
                  Examples
                </Text>
                {EXAMPLES[mode].map((ex) => (
                  <Text key={ex} fz="sm" c="violet.7" style={{ cursor: "pointer" }} onClick={() => run(ex)}>
                    “{ex}”
                  </Text>
                ))}
              </Stack>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {active.error && <ErrorNotice error={active.error} onRetry={() => run()} />}
          {active.isPending && <Skeleton h={220} radius="lg" />}
          {mode === "extract" && extract.data && !extract.isPending && (
            <Stack gap="sm">
              <ResultHeader title="Foods" count={extract.data.data.length} meta={extract.data.meta} feedback={<FeedbackButtons requestId={extract.data.meta.requestId} />} />
              <MealGroupsView items={extract.data.data} onSelect={setSelected} />
            </Stack>
          )}
          {mode === "structured" && structured.data && !structured.isPending && (
            <Stack gap="sm">
              <ResultHeader
                title="Meal"
                meta={structured.data.meta}
                feedback={<FeedbackButtons requestId={structured.data.meta.requestId} />}
              />
              <MealGroupsView items={flattenMealResult(structured.data.data)} onSelect={setSelected} fallbackMealName="Your meal" />
            </Stack>
          )}
          {mode === "actions" && actions.data && !actions.isPending && (
            <Stack gap="md">
              <ResultHeader title="Actions" count={actions.data.data.length} meta={actions.data.meta} feedback={<FeedbackButtons requestId={actions.data.meta.requestId} />} />
              {actions.data.data.length === 0 && <EmptyState title="No log actions found" />}
              {actions.data.data.map((a, i) => (
                <Stack key={i} gap="xs">
                  <Group gap="xs">
                    <Badge size="lg" color={actionColor[a.action ?? "none"]} tt="uppercase">
                      {a.action ?? "none"}
                    </Badge>
                    {a.mealTime && a.mealTime !== "none" && (
                      <Title order={3} fz={18} tt="capitalize">
                        {a.mealTime}
                      </Title>
                    )}
                    {a.date && <Badge variant="outline" color="gray">{a.date}</Badge>}
                  </Group>
                  <MealGroupsView items={a.items ?? []} onSelect={setSelected} fallbackMealName={a.mealTime && a.mealTime !== "none" ? a.mealTime : "Items"} emptyText="No items in this action." />
                </Stack>
              ))}
            </Stack>
          )}
          {!active.data && !active.isPending && !active.error && <EmptyState title="Try an example">Mixed meals show how meal / component names come back on the regular (non meal-structured) route.</EmptyState>}
        </Grid.Col>
      </Grid>
      <FoodDetailDrawer refCode={selected?.refCode} name={selected?.displayName} amount={selected} onClose={() => setSelected(null)} />
    </RequireFeature>
  );
}
